import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { Access } from 'modules/core/hooks/useCurrentSession'
import { dateToString } from 'modules/core/lib/dates'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import type { TeamViewEmployee } from 'modules/calendar/components/TeamView'
import { calendarEventEmployee } from 'modules/calendar/lib/calendarEventEmployee'
import {
  calendarEventCanReadLeaves,
  calendarEventCanSeeTimeoffTypes,
} from 'modules/calendar/lib/calendarLeaves'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'
import employees from 'modules/employees/store/collections/employees'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'

export interface DayLeaf {
  classes: {
    companyHoliday: boolean
    isToday: boolean
  }
  date: moment.Moment
}

export interface LeaveEventLeaf {
  contentClasses: {
    halfDay: boolean
    hasLeave: boolean
    canSeeLeave: boolean
    canManageLeave: boolean
    default: boolean
    firstHalf: boolean
    secondHalf: boolean
  }
  data: {
    leaveId: string
    leaveTypeName: string | null | undefined
    leaveDescriptionTooltip: string | null | undefined
    canManageLeave: boolean
    approved: boolean
    color: string | null | undefined
  }
}

export type LeaveEventLeaves = LeaveEventLeaf[]

class Store {
  date: moment.Moment
  filteredEmployees: TeamViewEmployee[]
  access: Access

  constructor(
    date: moment.Moment,
    filteredEmployees: TeamViewEmployee[] = [],
    currentAccess: Access
  ) {
    makeObservable(this, {
      daysInMonth: computed,
      days: computed,
      leaves: computed,
      employeeLocationIds: computed,
      companyHolidayLocationIdsByDate: computed,
    })
    this.date = date
    this.filteredEmployees = filteredEmployees
    this.access = currentAccess
  }

  get daysInMonth(): number {
    return this.date.daysInMonth()
  }

  get employeeLocationIds(): Set<string> {
    return new Set(
      this.filteredEmployees.map((item) => item.locationId).filter((id): id is string => id != null)
    )
  }

  get companyHolidayLocationIdsByDate(): Record<string, Set<string>> {
    const employeeLocationIds = this.employeeLocationIds

    return calendarEvents.toArray().reduce(function (
      locationIdsByDate: Record<string, Set<string>>,
      item
    ) {
      const locationId = item.metadata('location_id')

      if (item.attributes.type === 'companyHoliday' && employeeLocationIds.has(locationId)) {
        const date = item.attributes.date
        locationIdsByDate[date] = locationIdsByDate[date] || new Set()
        locationIdsByDate[date]!.add(locationId)
      }

      return locationIdsByDate
    }, {})
  }

  /**
   * We are iterating N * M times (N = num employees, M = num events).
   * By indexing the companyHolidays in a tree we can get near constant lookups.
   *
   *   Tree shape:
   *
   *   [{ companyHoliday: true, isToday: true }]
   */
  get days(): DayLeaf[] {
    const today = moment()

    return _.range(this.daysInMonth).map((day: number) => {
      const date = moment([this.date.year(), this.date.month(), day + 1])
      const key = dateToString(date, 'backend')
      const companyHoliday = !!this.companyHolidayLocationIdsByDate[key]?.size
      const isToday = date.isSame(today, 'day')

      return {
        classes: {
          companyHoliday,
          isToday,
        },
        date,
      }
    })
  }

  /**
   * We are iterating N * M times (N = num employees, M = num events).
   * By indexing the leaves in a tree we can get near constant lookups.
   *
   *   Tree shape:
   *
   *   {
   *     day-employee_id: {
   *       dayCell: { ... }
   *     }
   *   }
   */
  get leaves(): Record<string, LeaveEventLeaves> {
    return calendarEvents.models.reduce(
      (memo: Record<string, LeaveEventLeaves>, event: CalendarEvent) => {
        if (event.attributes.type !== 'leave') return memo

        // Do not show rejected leaves. Pending leaves has approved as undefined
        const approved = event.metadata('approved')

        if (approved === false) return memo

        // Check permissions
        const canSeeType = calendarEventCanSeeTimeoffTypes(event)
        const canManageLeave = calendarEventCanReadLeaves(event)

        if (!canManageLeave) return memo

        if (!approved && !canSeeType) return memo

        const employee = calendarEventEmployee(event, employees)

        if (!employee) return memo

        const date = moment(event.attributes.date)
        const halfDay = event.metadata('half_day')
        const key = `${date.date() - 1}-${employee.attributes.id}`
        // Color is defined in the backend based on permissions
        const color = `#${event.metadata('leave_color')}`
        const isHourly = event.metadata('hourly')
        const leaveId = event.metadata('leave_id')
        let leaveTypeName = i18n.t('settings.leave_types.defaults.generic')
        let leaveDescriptionTooltip = null

        if (canManageLeave || canSeeType) {
          leaveTypeName = event.metadata('leave_type_name')
        }

        if (isHourly && canSeeType) {
          leaveDescriptionTooltip = i18n.tp('time_off.tooltip_leave_hours', {
            count: event.metadata('hours_amount'),
            startTime: formatTime(this.access, event.metadata('start_time')),
          })
        }

        const eventData = {
          contentClasses: {
            halfDay,
            hasLeave: true,
            isNotApproved: !approved,
            canSeeLeave: canSeeType,
            canManageLeave,
            default: canSeeType ? event.leaveType === 'default' : true,
            firstHalf: halfDay === HALF_DAY_TYPES.beggining_of_day,
            secondHalf: halfDay === HALF_DAY_TYPES.end_of_day,
            isHourly,
          },
          data: {
            leaveId,
            leaveTypeName,
            leaveDescriptionTooltip,
            canManageLeave,
            approved,
            color,
          },
        }
        memo[key] !== undefined ? memo[key]!.push(eventData) : (memo[key] = [eventData])

        return memo
      },
      {}
    )
  }
}

export default Store
